import { useMutation, useQuery } from "@tanstack/react-query";
import { searchBroadcastsIndex } from "entities/algolia/constants";
import { Broadcast } from "entities/broadcasts/types";
import { debounce } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useInstantSearch, useSearchBox } from "react-instantsearch";
import { queryClient } from "shared/api/queryClient";

type PaginationModelType = {
  pageSize: number;
  page: number;
};

const initialPaginationModal = {
  pageSize: 25,
  page: 0,
};

export const invalidateBroadcastsData = async (
  query: string,
  paginationModel: PaginationModelType,
) => {
  return new Promise((resolve) => {
    setTimeout(async () => {
      await queryClient.invalidateQueries({
        queryKey: ["broadcasts", query, paginationModel],
      });
      resolve(true);
    }, 3000);
  });
};

export const useBroadcastsData = () => {
  const [paginationModel, setPaginationModel] = useState<PaginationModelType>(
    initialPaginationModal,
  );
  const { query, refine: refineSearch, clear } = useSearchBox();
  const { status } = useInstantSearch();

  useEffect(() => {
    return () => {
      clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onResetPage = () =>
    setPaginationModel((prev) => ({ ...prev, page: 0 }));

  const { data: response } = useQuery({
    queryKey: ["broadcasts", query, paginationModel],
    queryFn: async () => {
      return searchBroadcastsIndex.search<Broadcast>("", {
        query,
        hitsPerPage: paginationModel.pageSize,
        page: paginationModel.page,
        cacheable: false,
      });
    },
  });

  const data = useMemo(
    () =>
      response?.hits.map((hit) => ({
        ...hit,
        id: hit.objectID,
      })),
    [response?.hits],
  );

  const onSearch = debounce((value) => {
    refineSearch(value);
    onResetPage();
  }, 500);

  const isLoading = status === "loading";

  const { mutate: addBroadcast, isPending: isAddingBroadcast } = useMutation({
    mutationFn: async (newBroadcast: Broadcast) => {
      await new Promise((resolve) => setTimeout(resolve, 500));
      return newBroadcast;
    },
    onMutate: async (newBroadcast: Broadcast) => {
      await queryClient.cancelQueries({
        queryKey: ["broadcasts", query, paginationModel],
      });

      const previousResponse = queryClient.getQueryData([
        "broadcasts",
        query,
        paginationModel,
      ]);

      const broadcastWithIds = {
        ...newBroadcast,
        objectID: (newBroadcast as any).objectID || newBroadcast.id,
        id: newBroadcast.id,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      if (response) {
        const updatedResponse = {
          ...response,
          hits: [broadcastWithIds, ...response.hits],
          nbHits: (response.nbHits || 0) + 1,
        };

        queryClient.setQueryData(
          ["broadcasts", query, paginationModel],
          updatedResponse,
        );
      }

      return { previousResponse };
    },
    onError: (err, _newBroadcast, context) => {
      queryClient.setQueryData(
        ["broadcasts", query, paginationModel],
        context?.previousResponse,
      );
      console.error("Error adding broadcast:", err);
    },
    onSuccess: () => {
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: ["broadcasts", query, paginationModel],
        });
      }, 2000);
    },
  });

  const { mutate: updateBroadcast, isPending: isUpdatingBroadcast } =
    useMutation({
      mutationFn: async (updatedBroadcast: Broadcast) => {
        await new Promise((resolve) => setTimeout(resolve, 500));
        return updatedBroadcast;
      },
      onMutate: async (updatedBroadcast: Broadcast) => {
        if (!updatedBroadcast.id) return { previousResponse: null };

        await queryClient.cancelQueries({
          queryKey: ["broadcasts", query, paginationModel],
        });

        if (updatedBroadcast.id) {
          await queryClient.cancelQueries({
            queryKey: ["broadcast-details", updatedBroadcast.id],
          });
        }

        const previousResponse = queryClient.getQueryData([
          "broadcasts",
          query,
          paginationModel,
        ]);

        const previousDetails = updatedBroadcast.id
          ? queryClient.getQueryData(["broadcast-details", updatedBroadcast.id])
          : null;

        if (response?.hits) {
          const index = response.hits.findIndex(
            (broadcast) => broadcast.objectID === updatedBroadcast.id,
          );

          if (index === -1) {
            const broadcastWithIds = {
              ...updatedBroadcast,
              objectID: updatedBroadcast.id,
              id: updatedBroadcast.id,
              updatedAt: new Date(),
            };

            const updatedResponse = {
              ...response,
              hits: [broadcastWithIds, ...response.hits],
              nbHits: (response.nbHits || 0) + 1,
            };

            queryClient.setQueryData(
              ["broadcasts", query, paginationModel],
              updatedResponse,
            );
          } else {
            const broadcastWithIds = {
              ...updatedBroadcast,
              objectID: updatedBroadcast.id,
              id: updatedBroadcast.id,
              updatedAt: new Date(),
            };

            const updatedHits = [...response.hits];
            updatedHits[index] = broadcastWithIds;

            const updatedResponse = {
              ...response,
              hits: updatedHits,
            };

            queryClient.setQueryData(
              ["broadcasts", query, paginationModel],
              updatedResponse,
            );
          }

          if (updatedBroadcast.id) {
            queryClient.setQueryData(
              ["broadcast-details", updatedBroadcast.id],
              updatedBroadcast,
            );
          }
        }

        return {
          previousResponse,
          previousDetails,
          broadcastId: updatedBroadcast.id,
        };
      },
      onError: (err, updatedBroadcast, context) => {
        if (context?.previousResponse) {
          queryClient.setQueryData(
            ["broadcasts", query, paginationModel],
            context.previousResponse,
          );
        }

        if (context?.broadcastId && context?.previousDetails) {
          queryClient.setQueryData(
            ["broadcast-details", context.broadcastId],
            context.previousDetails,
          );
        }

        console.error("Error updating broadcast:", err);
      },
      onSuccess: (data, variables) => {
        const broadcastId = variables.id;

        setTimeout(async () => {
          await queryClient.invalidateQueries({
            queryKey: ["broadcasts", query, paginationModel],
          });

          if (broadcastId) {
            await queryClient.invalidateQueries({
              queryKey: ["broadcast-details", broadcastId],
            });

            await queryClient.invalidateQueries({
              queryKey: [["broadcast-image-file", broadcastId]],
            });
          }
        }, 2000);
      },
    });

  return {
    searchData: {
      value: query,
      onChange: onSearch,
    },
    tableData: {
      data: data ?? [],
      rowCount: response?.nbHits ?? 0,
      paginationModel: paginationModel,
      onPaginationModelChange: setPaginationModel,
    },
    isLoading,
    onResetPage,
    addBroadcastOptimistically: (broadcast: Broadcast) =>
      addBroadcast(broadcast),
    updateBroadcastOptimistically: (broadcast: Broadcast) =>
      updateBroadcast(broadcast),
    addBroadcast,
    updateBroadcast,
    isAddingBroadcast,
    isUpdatingBroadcast,
  };
};
